import { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

// material-ui
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha, useTheme } from '@mui/material/styles';

// ant design icons
import DownOutlined from '@ant-design/icons/DownOutlined';
import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import RightOutlined from '@ant-design/icons/RightOutlined';
import SearchOutlined from '@ant-design/icons/SearchOutlined';

// project imports
import { updateTarget } from 'services/api/iep';
import usePageAccess from 'hooks/usePageAccess';
import useOnlineStatus from 'hooks/useOnlineStatus';
import {
  TargetDialog,
  TARGET_STATUS_INTL_KEYS,
  backendTargetToDraft,
  computeDays,
  draftToTargetPayload
} from 'sections/shared/child-detail/GoalsTargetsTab';
import TargetStatusHistoryButton from 'sections/shared/child-detail/TargetStatusHistory';

const FILTER_ALL = 'all';
const FILTER_MASTERED = 'mastered';
const FILTER_IN_PROGRESS = 'in_progress';

// 'YYYY-MM-DD' for a date-only or ISO string; '' when absent.
const isoDay = (v) => (v ? String(v).slice(0, 10) : '');

// End of the session's scheduled day (or today): a target introduced ON that day
// counts, one introduced after it does not - the same cutoff the other sheets use.
const visibilityCutoff = (sessionDate) => {
  const d = sessionDate ? new Date(sessionDate) : new Date();
  if (Number.isNaN(d.getTime())) return isoDay(new Date().toISOString());
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// CSV cell: quoted, quotes doubled, and a leading = + - @ (or tab/CR) neutralised
// so a target name cannot run as a formula when the file is opened in Excel
// (CSV / formula injection, OWASP A03).
const csvCell = (value) => {
  let v = value === null || value === undefined ? '' : String(value);
  if (/^[=+\-@\t\r]/.test(v)) v = `'${v}`;
  return `"${v.replace(/"/g, '""')}"`;
};

/**
 * Target Tracking data sheet.
 *
 * Lists the child's targets from the IEP version in effect, grouped by domain:
 * name, introduced date, mastered date, status and days to mastery (calculated).
 * Every target introduced on/before the session's scheduled date is listed - an
 * unmastered one appears automatically once its introduced date arrives - and a
 * filter narrows the list to Mastered / In Progress / All.
 *
 * Edit opens the Goals & Targets tab's own TargetDialog and saves through the same
 * PUT, so a change made here IS the change on the child's Goals & Targets (same
 * row, same status-history entry). Targets cannot be added or deleted here.
 *
 * Nothing is recorded per session: this sheet edits the plan, it does not collect
 * session data, so it never calls onDataChange. The list comes from the clinical
 * bundle (cached), so it opens and exports offline; editing needs the network.
 */
const TargetTrackingMode = ({
  trackingTargets = [],
  sessionDate = null,
  childId = '',
  childName = '',
  skills = [],
  onTargetsChanged,
  // When provided, an "Add Target" button appears top-right. `addTargetGoals` is the
  // child's IEP goals the new target can attach to; `onAddTarget(draft, selectedGoal)`
  // persists it (same contract the Add Target shortcut uses).
  addTargetGoals = null,
  onAddTarget = null
}) => {
  const theme = useTheme();
  const intl = useIntl();
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));
  // Phones and tablets get a compact toolbar: filters side by side, icon actions
  // next to search. On phones Add Target also takes its own full-width row.
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const compactToolbar = isCompact;
  const { isOnline } = useOnlineStatus();
  const { readOnly: isCodeReadOnly } = usePageAccess();
  const canEdit = !isCodeReadOnly('child_goals_targets');

  const [filter, setFilter] = useState(FILTER_IN_PROGRESS);
  const [domainFilter, setDomainFilter] = useState('all');
  const [search, setSearch] = useState('');
  // Add Target dialog (goal-select mode), shown only when the parent wires it up.
  const [adding, setAdding] = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  // Synchronous guard so rapid double/triple clicks can't fire createTarget twice
  // before React re-renders the disabled button.
  const addSavingRef = useRef(false);
  const canAddTarget =
    typeof onAddTarget === 'function' && Array.isArray(addTargetGoals) && addTargetGoals.length > 0 && isOnline && canEdit;
  // Column sort within each domain group. Default: introduced date, ascending.
  const [sort, setSort] = useState({ by: 'introduced', dir: 'asc' });
  const handleSort = (by) => setSort((s) => ({ by, dir: s.by === by && s.dir === 'asc' ? 'desc' : 'asc' }));
  const [editing, setEditing] = useState(null); // a trackingTargets entry
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  // Domain groups the user has expanded: { [domain]: true }. Groups are COLLAPSED
  // by default - a domain absent from this map stays collapsed.
  const [expanded, setExpanded] = useState({});
  const toggleGroup = (domain) => setExpanded((prev) => ({ ...prev, [domain]: !prev[domain] }));

  const cutoff = visibilityCutoff(sessionDate);

  // Introduced on/before the session date; status derived exactly as the Goals &
  // Targets tab does (a mastered date means mastered).
  const rows = useMemo(
    () =>
      (trackingTargets || [])
        .map((entry) => ({ entry, draft: backendTargetToDraft(entry.row) }))
        .filter(({ draft }) => draft.dateIntroduced && isoDay(draft.dateIntroduced) <= cutoff)
        .map(({ entry, draft }) => ({ entry, draft, mastered: draft.status === 'mastered', days: computeDays(draft) })),
    [trackingTargets, cutoff]
  );

  const counts = useMemo(
    () => ({ all: rows.length, mastered: rows.filter((r) => r.mastered).length, inProgress: rows.filter((r) => !r.mastered).length }),
    [rows]
  );

  // Domain name used for both grouping and the domain filter - kept identical so
  // the dropdown value matches a group header.
  const domainOf = useCallback((r) => r.entry.domainName || intl.formatMessage({ id: 'iep.goalDomainUnassigned' }), [intl]);

  // Dropdown options are the domains actually shown in the list - i.e. this child's
  // targets introduced on/before the session date (`rows`). Independent of the
  // Show/search filters, but it honours the same cutoff as the groups so the two
  // never disagree (a not-yet-introduced domain won't appear here either).
  const domainOptions = useMemo(() => [...new Set(rows.map(domainOf))].sort((a, b) => a.localeCompare(b)), [rows, domainOf]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === FILTER_MASTERED && !r.mastered) return false;
      if (filter === FILTER_IN_PROGRESS && r.mastered) return false;
      if (domainFilter !== 'all' && domainOf(r) !== domainFilter) return false;
      if (q && !`${r.draft.name} ${r.entry.domainName} ${r.entry.goalLabel}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, filter, domainFilter, search, domainOf]);

  // Domain groups, alphabetical; targets inside by introduced date, then name.
  const groups = useMemo(() => {
    const byDomain = new Map();
    visible.forEach((r) => {
      const key = r.entry.domainName || intl.formatMessage({ id: 'iep.goalDomainUnassigned' });
      if (!byDomain.has(key)) byDomain.set(key, []);
      byDomain.get(key).push(r);
    });
    return [...byDomain.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([domain, list]) => ({
        domain,
        list: list.sort(
          (a, b) => isoDay(a.draft.dateIntroduced).localeCompare(isoDay(b.draft.dateIntroduced)) || a.draft.name.localeCompare(b.draft.name)
        )
      }));
  }, [visible, intl]);

  // Global expand/collapse across every visible group.
  const allExpanded = groups.length > 0 && groups.every((g) => expanded[g.domain]);
  const allCollapsed = groups.every((g) => !expanded[g.domain]);
  const expandAll = () => setExpanded(Object.fromEntries(groups.map((g) => [g.domain, true])));
  const collapseAll = () => setExpanded({});

  const statusLabel = (status) =>
    TARGET_STATUS_INTL_KEYS[status] ? intl.formatMessage({ id: TARGET_STATUS_INTL_KEYS[status] }) : String(status || '').replace(/_/g, ' ');

  const daysLabel = (r) => (r.days === null || r.days === undefined ? '—' : `${r.days}d`);

  // Sort a group's rows by the active column. Blank days always sort last; every
  // sort falls back to target name so the order is stable.
  const sortValue = (r, by) => {
    switch (by) {
      case 'target':
        return (r.draft.name || '').toLowerCase();
      case 'status':
        return statusLabel(r.draft.status).toLowerCase();
      case 'mastered':
        return isoDay(r.draft.dateMastered) || '';
      case 'days':
        return r.days === null || r.days === undefined ? null : r.days;
      case 'introduced':
      default:
        return isoDay(r.draft.dateIntroduced) || '';
    }
  };

  const sortRows = (list) => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      const va = sortValue(a, sort.by);
      const vb = sortValue(b, sort.by);
      if (sort.by === 'days') {
        if (va === null && vb === null) return (a.draft.name || '').localeCompare(b.draft.name || '');
        if (va === null) return 1; // blanks last, regardless of direction
        if (vb === null) return -1;
      }
      let c = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb));
      if (c === 0) c = (a.draft.name || '').localeCompare(b.draft.name || '');
      return c * dir;
    });
  };

  const sortableHeader = (key, labelId, labelDefault, align) => (
    <TableCell align={align} sortDirection={sort.by === key ? sort.dir : false}>
      <TableSortLabel
        active={sort.by === key}
        direction={sort.by === key ? sort.dir : 'asc'}
        onClick={() => handleSort(key)}
        // Always show a faint arrow on inactive columns so every column reads as sortable.
        sx={{ '& .MuiTableSortLabel-icon': { opacity: sort.by === key ? 1 : 0.35 } }}
      >
        {intl.formatMessage({ id: labelId, defaultMessage: labelDefault })}
      </TableSortLabel>
    </TableCell>
  );

  const handleExport = () => {
    const header = [
      intl.formatMessage({ id: 'targetTracking.col.domain', defaultMessage: 'Domain' }),
      intl.formatMessage({ id: 'targetTracking.col.goal', defaultMessage: 'Goal' }),
      intl.formatMessage({ id: 'targetTracking.col.target', defaultMessage: 'Target' }),
      intl.formatMessage({ id: 'targetTracking.col.introduced', defaultMessage: 'Introduced' }),
      intl.formatMessage({ id: 'targetTracking.col.mastered', defaultMessage: 'Mastered' }),
      intl.formatMessage({ id: 'targetTracking.col.status', defaultMessage: 'Status' }),
      intl.formatMessage({ id: 'targetTracking.col.days', defaultMessage: 'Days to Mastery' })
    ];
    const lines = [header.map(csvCell).join(',')];
    groups.forEach(({ domain, list }) =>
      list.forEach((r) =>
        lines.push(
          [
            domain,
            r.entry.goalLabel,
            r.draft.name,
            isoDay(r.draft.dateIntroduced),
            isoDay(r.draft.dateMastered),
            statusLabel(r.draft.status),
            r.days ?? ''
          ]
            .map(csvCell)
            .join(',')
        )
      )
    );
    // BOM so Excel opens non-ASCII names (e.g. Hindi / Tamil) as UTF-8.
    const blob = new Blob([`﻿${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const who = String(childName || 'child')
      .trim()
      .replace(/[^\w-]+/g, '-')
      .replace(/^-+|-+$/g, '');
    a.href = url;
    a.download = `target-tracking-${who || 'child'}-${isoDay(new Date().toISOString())}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleSave = async (saved) => {
    if (!editing) return;
    setSaving(true);
    setError(null);
    try {
      await updateTarget(editing.iepId, editing.goalRowId, editing.row.id, draftToTargetPayload(saved));
      setNotice(
        intl.formatMessage(
          { id: 'targetTracking.saved', defaultMessage: '"{name}" updated - the change is on the child\'s Goals & Targets.' },
          { name: (saved.name || '').trim() || editing.row.title }
        )
      );
      setEditing(null);
      if (onTargetsChanged) await onTargetsChanged();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to save target');
    } finally {
      setSaving(false);
    }
  };

  // Save a brand-new target via the parent's handler (it persists + reloads the bundle
  // so trackingTargets here refreshes). We close the dialog and show a success Alert so
  // the collector gets clear confirmation; errors surface in the error Alert.
  const handleAddSave = async (saved, selectedGoal) => {
    // Ignore repeat clicks while a save is already in flight (prevents duplicate targets).
    if (!onAddTarget || addSavingRef.current) return;
    addSavingRef.current = true;
    setAddSaving(true);
    setError(null);
    setNotice(null);
    try {
      await onAddTarget(saved, selectedGoal);
      setAdding(false);
      setNotice(
        intl.formatMessage(
          { id: 'targetTracking.added', defaultMessage: '"{name}" added to the child\'s Goals & Targets.' },
          { name: (saved?.name || '').trim() }
        )
      );
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to add target');
    } finally {
      addSavingRef.current = false;
      setAddSaving(false);
    }
  };

  const editButton = (r) =>
    canEdit && (
      <Tooltip
        title={
          isOnline
            ? intl.formatMessage({ id: 'targetTracking.edit', defaultMessage: 'Edit target' })
            : intl.formatMessage({ id: 'targetTracking.editOffline', defaultMessage: 'Editing needs a connection' })
        }
      >
        <span>
          <IconButton size="small" color="primary" disabled={!isOnline || saving} onClick={() => setEditing(r.entry)}>
            <EditOutlined />
          </IconButton>
        </span>
      </Tooltip>
    );

  const statusChip = (r) => (
    <Chip
      size="small"
      variant="outlined"
      color={r.mastered ? 'success' : r.draft.status === 'on_hold' ? 'warning' : 'primary'}
      label={statusLabel(r.draft.status)}
    />
  );

  const daysChip = (r) => (
    <Chip
      size="small"
      label={daysLabel(r)}
      sx={{
        fontWeight: 600,
        bgcolor: r.mastered ? alpha(theme.palette.success.main, 0.15) : alpha(theme.palette.grey[500], 0.12),
        color: r.mastered ? 'success.dark' : 'text.primary'
      }}
    />
  );

  const domainSelect = (
    <TextField
      select
      size="small"
      label={intl.formatMessage({ id: 'targetTracking.domain', defaultMessage: 'Domain' })}
      value={domainFilter}
      onChange={(e) => setDomainFilter(e.target.value)}
      sx={compactToolbar ? { flex: 1, minWidth: 0 } : { minWidth: 180 }}
    >
      <MenuItem value="all">{intl.formatMessage({ id: 'targetTracking.domain.all', defaultMessage: 'All domains' })}</MenuItem>
      {domainOptions.map((d) => (
        <MenuItem key={d} value={d}>
          {d}
        </MenuItem>
      ))}
    </TextField>
  );

  const showSelect = (
    <TextField
      select
      size="small"
      label={intl.formatMessage({ id: 'targetTracking.show', defaultMessage: 'Show' })}
      value={filter}
      onChange={(e) => setFilter(e.target.value)}
      sx={compactToolbar ? { flex: 1, minWidth: 0 } : { minWidth: 220 }}
    >
      <MenuItem value={FILTER_IN_PROGRESS}>
        {intl.formatMessage({ id: 'targetTracking.filter.inProgress', defaultMessage: 'In Progress targets' })} ({counts.inProgress})
      </MenuItem>
      <MenuItem value={FILTER_MASTERED}>
        {intl.formatMessage({ id: 'targetTracking.filter.mastered', defaultMessage: 'Mastered targets' })} ({counts.mastered})
      </MenuItem>
      <MenuItem value={FILTER_ALL}>
        {intl.formatMessage({ id: 'targetTracking.filter.all', defaultMessage: 'All targets' })} ({counts.all})
      </MenuItem>
    </TextField>
  );

  const searchField = (
    <TextField
      size="small"
      fullWidth
      placeholder={intl.formatMessage({ id: 'targetTracking.search', defaultMessage: 'Search targets' })}
      sx={{ flex: '1 1 200px', minWidth: compactToolbar ? 0 : 200 }}
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlined />
            </InputAdornment>
          )
        }
      }}
    />
  );

  const addTargetButton = canAddTarget && (
    <Button
      variant="contained"
      startIcon={<PlusOutlined />}
      onClick={() => setAdding(true)}
      fullWidth={isPhone}
      sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
    >
      {intl.formatMessage({ id: 'targetTracking.addTarget', defaultMessage: 'Add Target' })}
    </Button>
  );

  // Phones / tablets: filters side by side; search with an expand/collapse toggle and an
  // export icon (+ Add Target on tablets); on phones Add Target gets its own row.
  const compactBar = (
    <Stack spacing={1.25}>
      <Stack direction="row" spacing={1}>
        {domainSelect}
        {showSelect}
      </Stack>
      <Stack direction="row" spacing={0.5} alignItems="center">
        {searchField}
        <Tooltip
          title={
            allExpanded
              ? intl.formatMessage({ id: 'targetTracking.collapseAll', defaultMessage: 'Collapse all' })
              : intl.formatMessage({ id: 'targetTracking.expandAll', defaultMessage: 'Expand all' })
          }
        >
          <span>
            <IconButton
              color="primary"
              onClick={allExpanded ? collapseAll : expandAll}
              disabled={groups.length === 0}
              aria-label={
                allExpanded
                  ? intl.formatMessage({ id: 'targetTracking.collapseAll', defaultMessage: 'Collapse all' })
                  : intl.formatMessage({ id: 'targetTracking.expandAll', defaultMessage: 'Expand all' })
              }
            >
              {allExpanded ? <RightOutlined /> : <DownOutlined />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={intl.formatMessage({ id: 'targetTracking.exportCsv', defaultMessage: 'Export CSV' })}>
          <span>
            <IconButton
              color="primary"
              onClick={handleExport}
              disabled={visible.length === 0}
              aria-label={intl.formatMessage({ id: 'targetTracking.exportCsv', defaultMessage: 'Export CSV' })}
            >
              <DownloadOutlined />
            </IconButton>
          </span>
        </Tooltip>
        {!isPhone && addTargetButton}
      </Stack>
      {isPhone && addTargetButton}
    </Stack>
  );

  return (
    <Stack spacing={2}>
      {/* Toolbar: filter + search + expand/collapse + export (+ Add Target). */}
      {compactToolbar ? (
        compactBar
      ) : (
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          {domainSelect}
          {showSelect}
          {searchField}
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownOutlined />}
            onClick={expandAll}
            disabled={groups.length === 0 || allExpanded}
            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            {intl.formatMessage({ id: 'targetTracking.expandAll', defaultMessage: 'Expand all' })}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RightOutlined />}
            onClick={collapseAll}
            disabled={groups.length === 0 || allCollapsed}
            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            {intl.formatMessage({ id: 'targetTracking.collapseAll', defaultMessage: 'Collapse all' })}
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadOutlined />}
            onClick={handleExport}
            disabled={visible.length === 0}
            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            {intl.formatMessage({ id: 'targetTracking.exportCsv', defaultMessage: 'Export CSV' })}
          </Button>
          {addTargetButton}
        </Stack>
      )}

      {!isOnline && canEdit && (
        <Alert severity="info">
          {intl.formatMessage({
            id: 'targetTracking.offline',
            defaultMessage: 'Offline: targets are shown from the last sync and can be exported. Editing needs a connection.'
          })}
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      {groups.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {rows.length === 0
              ? intl.formatMessage({
                  id: 'targetTracking.empty',
                  defaultMessage: 'No targets have been introduced for this child as of this session date.'
                })
              : intl.formatMessage({ id: 'targetTracking.noMatch', defaultMessage: 'No targets match this filter.' })}
          </Typography>
        </Paper>
      ) : (
        groups.map(({ domain, list: rawList }) => {
          const isCollapsed = !expanded[domain];
          const list = sortRows(rawList);
          return (
            <Paper key={domain} variant="outlined" sx={{ overflow: 'hidden' }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                onClick={() => toggleGroup(domain)}
                role="button"
                aria-expanded={!isCollapsed}
                sx={{
                  px: 2,
                  py: 1.25,
                  cursor: 'pointer',
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  borderBottom: isCollapsed ? 'none' : `1px solid ${theme.palette.divider}`,
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) }
                }}
              >
                <IconButton
                  size="small"
                  sx={{ p: 0.25 }}
                  aria-label={intl.formatMessage(
                    isCollapsed
                      ? { id: 'targetTracking.expandGroup', defaultMessage: 'Expand' }
                      : { id: 'targetTracking.collapseGroup', defaultMessage: 'Collapse' }
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleGroup(domain);
                  }}
                >
                  {isCollapsed ? <RightOutlined style={{ fontSize: 12 }} /> : <DownOutlined style={{ fontSize: 12 }} />}
                </IconButton>
                <Typography variant="subtitle1" fontWeight={600}>
                  {domain}
                </Typography>
                <Chip size="small" label={list.length} />
              </Stack>

              <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
                {isCompact ? (
                  // Phones / tablets in portrait: one card per target.
                  <Stack spacing={1} sx={{ p: 1.5 }}>
                    {list.map((r) => (
                      <Card key={r.entry.row.id} variant="outlined" sx={{ p: 1.5 }}>
                        <Stack direction="row" alignItems="flex-start" spacing={1}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ wordBreak: 'break-word' }}>
                              {r.draft.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {r.entry.goalLabel}
                            </Typography>
                          </Box>
                          {editButton(r)}
                        </Stack>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1.25 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {intl.formatMessage({ id: 'targetTracking.col.introduced', defaultMessage: 'Introduced' })}
                            </Typography>
                            <Typography variant="body2">{isoDay(r.draft.dateIntroduced) || '—'}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {intl.formatMessage({ id: 'targetTracking.col.mastered', defaultMessage: 'Mastered' })}
                            </Typography>
                            <Typography variant="body2">{isoDay(r.draft.dateMastered) || '—'}</Typography>
                          </Box>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.25 }}>
                          {statusChip(r)}
                          {daysChip(r)}
                        </Stack>
                      </Card>
                    ))}
                  </Stack>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {sortableHeader('target', 'targetTracking.col.target', 'Target')}
                          {sortableHeader('status', 'targetTracking.col.status', 'Status')}
                          {sortableHeader('introduced', 'targetTracking.col.introduced', 'Introduced')}
                          {sortableHeader('mastered', 'targetTracking.col.mastered', 'Mastered')}
                          {sortableHeader('days', 'targetTracking.col.days', 'Days to Mastery', 'center')}
                          <TableCell align="right" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {list.map((r) => (
                          <TableRow key={r.entry.row.id} hover>
                            <TableCell sx={{ maxWidth: 420 }}>
                              <Typography variant="subtitle2">{r.draft.name}</Typography>
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                {r.entry.goalLabel}
                              </Typography>
                            </TableCell>
                            <TableCell>{statusChip(r)}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{isoDay(r.draft.dateIntroduced) || '—'}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{isoDay(r.draft.dateMastered) || '—'}</TableCell>
                            <TableCell align="center">{daysChip(r)}</TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              {isOnline && <TargetStatusHistoryButton targetId={r.entry.row.id} targetName={r.draft.name} size="small" />}
                              {editButton(r)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Collapse>
            </Paper>
          );
        })
      )}

      {/* The Goals & Targets tab's Edit Target dialog - same fields, same save. */}
      <TargetDialog
        open={Boolean(editing)}
        onClose={() => !saving && setEditing(null)}
        onSave={handleSave}
        initial={editing ? backendTargetToDraft(editing.row) : null}
        goalDomainId={editing?.domainCode ? `DOMAIN-${editing.domainCode}` : ''}
        goalLabel={editing?.goalLabel || ''}
        childId={childId}
        skills={skills}
        saving={saving}
      />

      {/* Add Target dialog - goal-select mode, same dialog the Add Target shortcut opens. */}
      {canAddTarget && (
        <TargetDialog
          open={adding}
          onClose={() => !addSaving && setAdding(false)}
          onSave={handleAddSave}
          goals={addTargetGoals}
          childId={childId}
          skills={skills}
          saving={addSaving}
        />
      )}
    </Stack>
  );
};

TargetTrackingMode.propTypes = {
  // clinical bundle `trackingTargets`: { row, iepId, goalRowId, goalCode, goalLabel, domainCode, domainName }
  trackingTargets: PropTypes.array,
  // The session's scheduled date - targets introduced on/before it are listed.
  sessionDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  childId: PropTypes.string,
  childName: PropTypes.string,
  // Skill options for the Edit dialog (same list the Add Target shortcut uses).
  skills: PropTypes.array,
  // Reload the clinical bundle after a save so every sheet sees the change.
  onTargetsChanged: PropTypes.func,
  // Add Target wiring (optional): goals to attach to + the persist handler.
  addTargetGoals: PropTypes.array,
  onAddTarget: PropTypes.func
};

export default TargetTrackingMode;
